'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import LanguageToggle from '@/components/ui/LanguageToggle';
import { createClient } from '@/lib/supabase/client';
import {
  Fuel, ChevronRight, ChevronLeft, CheckCircle2, Loader2,
  AlertCircle, Lock, Eye, EyeOff, Mail, User, Building2, Globe, Hash, MapPin,
  Upload, FileText, X,
} from 'lucide-react';

// Steps: Account → Company → Office → Capabilities → ID Document → Review
const STEPS_EN = ['Account', 'Company', 'Office', 'Capabilities', 'ID Document', 'Review'];
const STEPS_AR = ['الحساب', 'الشركة', 'المكتب', 'القدرات', 'وثيقة الهوية', 'المراجعة'];

const BUNKER_TYPES = ['VLSFO', 'HSFO', 'MGO', 'LSMGO', 'LNG', 'Biofuel'];
const SERVICE_ZONES = ['Rotterdam', 'Singapore', 'Fujairah', 'Houston', 'Gibraltar', 'Piraeus', 'Shanghai', 'Dubai', 'Antwerp', 'Hamburg'];

export default function SupplierRegistrationWizard() {
  const [lang, setLang] = useState<'en' | 'ar'>('en');
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const [selectedFuels, setSelectedFuels] = useState<string[]>([]);
  const [idDocumentUrl, setIdDocumentUrl] = useState<string | null>(null);
  const [idDocumentName, setIdDocumentName] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const supabase = createClient();

  useEffect(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('nautex_lang') : null;
    if (saved === 'en' || saved === 'ar') setLang(saved);
  }, []);

  const handleLangChange = (l: 'en' | 'ar') => {
    setLang(l);
    if (typeof window !== 'undefined') localStorage.setItem('nautex_lang', l);
  };

  const { register, handleSubmit, formState: { errors }, trigger, watch, getValues } = useForm({ mode: 'onTouched' });
  const steps = lang === 'ar' ? STEPS_AR : STEPS_EN;
  const password = watch('password');

  const stepFields: Record<number, string[]> = {
    1: ['full_name', 'email', 'password', 'confirm_password'],
    2: ['company_name', 'country', 'registration_number'],
    3: ['office_name', 'office_city'],
    4: [],
    5: [],  // ID document — validated separately
  };

  const handleNext = async () => {
    // Step 5 (ID Document) — require upload before proceeding
    if (step === 5 && !idDocumentUrl) {
      toast.error(lang === 'ar' ? 'يرجى رفع وثيقة الهوية' : 'Please upload your ID document before proceeding');
      return;
    }
    const fields = stepFields[step] || [];
    if (fields.length > 0) {
      const valid = await trigger(fields);
      if (!valid) return;
    }
    setStep(s => Math.min(s + 1, steps.length));
  };

  const handleBack = () => setStep(s => Math.max(s - 1, 1));

  const toggleZone = (z: string) => setSelectedZones(prev => prev.includes(z) ? prev.filter(x => x !== z) : [...prev, z]);
  const toggleFuel = (f: string) => setSelectedFuels(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);

  const handleDocumentUpload = async (file: File) => {
    setUploadingDoc(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `id-docs/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { data, error } = await supabase.storage
        .from('member-documents')
        .upload(fileName, file, { upsert: false });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from('member-documents').getPublicUrl(fileName);
      setIdDocumentUrl(urlData.publicUrl);
      setIdDocumentName(file.name);
      toast.success(lang === 'ar' ? 'تم رفع الوثيقة بنجاح' : 'Document uploaded successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed. Please try again.');
    } finally {
      setUploadingDoc(false);
    }
  };

  const onSubmit = async (data: any) => {
    if (!idDocumentUrl) {
      toast.error(lang === 'ar' ? 'يرجى رفع وثيقة الهوية' : 'ID document is required');
      setStep(5);
      return;
    }
    setSubmitting(true);
    try {
      // All registration goes through the server-side /api/auth/register endpoint.
      // JWT role claim is NOT written here — only at activation after sanctions clearance.
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          full_name: data.full_name,
          role_intent: 'supplier',
          company_name: data.company_name,
          registration_number: data.registration_number || null,
          country: data.country,
          office_name: data.office_name,
          office_city: data.office_city,
          id_document_url: idDocumentUrl,
          service_zones: selectedZones,
          bunker_types: selectedFuels,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Registration failed');

      setSubmitted(true);
      setSubmittedId(json.registrationRef || `REG-${Date.now()}`);
      toast.success(lang === 'ar' ? 'تم تقديم التسجيل بنجاح' : 'Registration submitted successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen nautex-gradient-bg flex flex-col" dir={dir}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <Link href="/" className="flex items-center gap-2"><AppLogo size={28} /><span className="font-bold text-sm text-foreground">NautEX</span></Link>
          <LanguageToggle lang={lang} onChange={handleLangChange} />
        </div>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="card-surface max-w-md w-full p-8 text-center animate-scale-in">
            <div className="w-16 h-16 rounded-full bg-status-active flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">
              {lang === 'ar' ? 'تم تقديم التسجيل' : 'Registration Submitted'}
            </h2>
            <p className="text-xs font-medium text-emerald-400 mb-3">Supplier</p>
            <p className="text-sm text-muted-foreground mb-4">
              {lang === 'ar' ?'تم استلام طلبك وهو قيد مراجعة فريق NautEX. ستتلقى بريدًا إلكترونيًا لتأكيد عنوانك، ثم بريدًا آخر عند تفعيل حسابك.' :'Your registration has been received. Please check your email to confirm your address. You will receive another email once your account is activated by the NautEX team.'}
            </p>
            <div className="bg-muted/20 rounded-lg p-3 mb-6">
              <p className="text-xs text-muted-foreground">{lang === 'ar' ? 'رقم التسجيل' : 'Registration ID'}</p>
              <p className="font-mono text-sm font-bold text-primary mt-1">{submittedId}</p>
            </div>
            <Link href="/login" className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2">
              {lang === 'ar' ? 'العودة إلى تسجيل الدخول' : 'Back to Sign In'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen nautex-gradient-bg flex flex-col" dir={dir}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <Link href="/register" className="flex items-center gap-2"><AppLogo size={28} /><span className="font-bold text-sm text-foreground">NautEX</span></Link>
        <LanguageToggle lang={lang} onChange={handleLangChange} />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
              <Fuel size={20} className="text-emerald-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">{lang === 'ar' ? 'تسجيل المورد' : 'Supplier Registration'}</h1>
              <p className="text-xs text-muted-foreground">{lang === 'ar' ? `الخطوة ${step} من ${steps.length}` : `Step ${step} of ${steps.length}`}</p>
            </div>
          </div>

          {/* Step indicators */}
          <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1">
            {steps.map((s, i) => (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors flex-shrink-0 ${
                  i + 1 === step ? 'bg-emerald-500/20 text-emerald-400' :
                  i + 1 < step ? 'bg-status-active/20 text-status-active' : 'bg-muted/30 text-muted-foreground'
                }`}>
                  {i + 1 < step ? <CheckCircle2 size={12} /> : <span>{i + 1}</span>}
                  <span className="hidden sm:inline">{s}</span>
                </div>
                {i < steps.length - 1 && <div className="flex-1 h-px bg-border/40 min-w-2" />}
              </React.Fragment>
            ))}
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="card-surface p-6 space-y-4">
              {/* Step 1: Account */}
              {step === 1 && (
                <>
                  <h2 className="text-base font-semibold text-foreground mb-4">{lang === 'ar' ? 'إعداد الحساب' : 'Account Setup'}</h2>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{lang === 'ar' ? 'الاسم الكامل' : 'Full Name'}</label>
                    <div className="relative">
                      <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input className="input-base w-full pl-9 py-2.5 text-sm" placeholder={lang === 'ar' ? 'الاسم الكامل' : 'Your full name'}
                        {...register('full_name', { required: 'Full name is required' })} />
                    </div>
                    {errors.full_name && <p className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.full_name.message as string}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{lang === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}</label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input type="email" className="input-base w-full pl-9 py-2.5 text-sm" placeholder="you@company.com"
                        {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' } })} />
                    </div>
                    {errors.email && <p className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.email.message as string}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{lang === 'ar' ? 'كلمة المرور' : 'Password'}</label>
                    <div className="relative">
                      <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input type={showPassword ? 'text' : 'password'} className="input-base w-full pl-9 pr-10 py-2.5 text-sm" placeholder="Min. 8 characters"
                        {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Min. 8 characters' } })} />
                      <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {errors.password && <p className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.password.message as string}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{lang === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}</label>
                    <div className="relative">
                      <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input type={showConfirm ? 'text' : 'password'} className="input-base w-full pl-9 pr-10 py-2.5 text-sm" placeholder="Repeat password"
                        {...register('confirm_password', { required: 'Please confirm password', validate: v => v === password || 'Passwords do not match' })} />
                      <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {errors.confirm_password && <p className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.confirm_password.message as string}</p>}
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/25">
                    <p className="text-xs text-emerald-400">
                      {lang === 'ar' ?'ستتلقى بريدًا إلكترونيًا لتأكيد عنوانك بعد التسجيل. يُرجى التحقق من صندوق الوارد.' :'After registration, you will receive a confirmation email. Please check your inbox and confirm your email address.'}
                    </p>
                  </div>
                </>
              )}

              {/* Step 2: Company */}
              {step === 2 && (
                <>
                  <h2 className="text-base font-semibold text-foreground mb-4">{lang === 'ar' ? 'تفاصيل الشركة' : 'Company Details'}</h2>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{lang === 'ar' ? 'الاسم القانوني للشركة' : 'Company Legal Name'}</label>
                    <div className="relative">
                      <Building2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input className="input-base w-full pl-9 py-2.5 text-sm" placeholder={lang === 'ar' ? 'الاسم القانوني' : 'Legal company name'}
                        {...register('company_name', { required: 'Company name is required' })} />
                    </div>
                    {errors.company_name && <p className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.company_name.message as string}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{lang === 'ar' ? 'الدولة' : 'Country'}</label>
                    <div className="relative">
                      <Globe size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input className="input-base w-full pl-9 py-2.5 text-sm" placeholder="e.g. United Arab Emirates"
                        {...register('country', { required: 'Country is required' })} />
                    </div>
                    {errors.country && <p className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.country.message as string}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{lang === 'ar' ? 'رقم السجل التجاري' : 'CR / Trade Licence Number'}</label>
                    <div className="relative">
                      <Hash size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input className="input-base w-full pl-9 py-2.5 text-sm" placeholder="Registration number" {...register('registration_number')} />
                    </div>
                  </div>
                </>
              )}

              {/* Step 3: Office */}
              {step === 3 && (
                <>
                  <h2 className="text-base font-semibold text-foreground mb-4">{lang === 'ar' ? 'تفاصيل المكتب' : 'Office Details'}</h2>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{lang === 'ar' ? 'اسم المكتب' : 'Office Name'}</label>
                    <input className="input-base w-full py-2.5 text-sm" placeholder="e.g. Singapore Office"
                      {...register('office_name', { required: 'Office name is required' })} />
                    {errors.office_name && <p className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.office_name.message as string}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{lang === 'ar' ? 'المدينة / الميناء' : 'City / Port'}</label>
                    <div className="relative">
                      <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input className="input-base w-full pl-9 py-2.5 text-sm" placeholder="e.g. Singapore"
                        {...register('office_city', { required: 'City is required' })} />
                    </div>
                    {errors.office_city && <p className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.office_city.message as string}</p>}
                  </div>
                </>
              )}

              {/* Step 4: Capabilities */}
              {step === 4 && (
                <>
                  <h2 className="text-base font-semibold text-foreground mb-1">{lang === 'ar' ? 'قدرات التوريد' : 'Supply Capabilities'}</h2>
                  <p className="text-xs text-muted-foreground mb-4">{lang === 'ar' ? 'حدد مناطق الخدمة وأنواع الوقود التي تدعمها.' : 'Select the service zones and fuel grades you support.'}</p>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">{lang === 'ar' ? 'مناطق الخدمة' : 'Service Zones'}</label>
                    <div className="flex flex-wrap gap-2">
                      {SERVICE_ZONES.map(z => (
                        <button key={z} type="button" onClick={() => toggleZone(z)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                            selectedZones.includes(z) ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'border-border/60 text-muted-foreground hover:border-border hover:text-foreground'
                          }`}>
                          {z}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">{lang === 'ar' ? 'أنواع الوقود' : 'Bunker Types'}</label>
                    <div className="flex flex-wrap gap-2">
                      {BUNKER_TYPES.map(f => (
                        <button key={f} type="button" onClick={() => toggleFuel(f)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                            selectedFuels.includes(f) ? 'bg-primary/20 border-primary/50 text-primary' : 'border-border/60 text-muted-foreground hover:border-border hover:text-foreground'
                          }`}>
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Step 5: ID Document Upload */}
              {step === 5 && (
                <>
                  <h2 className="text-base font-semibold text-foreground mb-1">{lang === 'ar' ? 'وثيقة الهوية' : 'ID Document'}</h2>
                  <p className="text-xs text-muted-foreground mb-4">
                    {lang === 'ar' ?'مطلوب — يرجى رفع جواز السفر أو بطاقة الهوية الوطنية أو رخصة التجارة.' :'Required — please upload your passport, national ID, or trade licence.'}
                  </p>

                  {idDocumentUrl ? (
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-status-active/10 border border-status-active/30">
                      <FileText size={20} className="text-status-active flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{idDocumentName}</p>
                        <p className="text-xs text-status-active">{lang === 'ar' ? 'تم الرفع بنجاح' : 'Uploaded successfully'}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setIdDocumentUrl(null); setIdDocumentName(null); }}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-border hover:border-primary/50 rounded-xl p-8 text-center cursor-pointer transition-colors group"
                    >
                      {uploadingDoc ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 size={24} className="animate-spin text-primary" />
                          <p className="text-sm text-muted-foreground">{lang === 'ar' ? 'جارٍ الرفع...' : 'Uploading...'}</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload size={24} className="text-muted-foreground group-hover:text-primary transition-colors" />
                          <p className="text-sm font-medium text-foreground">{lang === 'ar' ? 'انقر لرفع الوثيقة' : 'Click to upload document'}</p>
                          <p className="text-xs text-muted-foreground">{lang === 'ar' ? 'PDF، JPG، PNG — حتى 10 ميغابايت' : 'PDF, JPG, PNG — up to 10 MB'}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleDocumentUpload(file);
                    }}
                  />

                  <div className="p-3 rounded-lg bg-muted/10 border border-border">
                    <p className="text-xs text-muted-foreground">
                      {lang === 'ar' ?'تُستخدم هذه الوثيقة للتحقق من هويتك خلال عملية مراجعة التسجيل. لن تُشارك مع أطراف ثالثة.' :'This document is used to verify your identity during the registration review process. It will not be shared with third parties.'}
                    </p>
                  </div>
                </>
              )}

              {/* Step 6: Review */}
              {step === 6 && (
                <>
                  <h2 className="text-base font-semibold text-foreground mb-4">{lang === 'ar' ? 'مراجعة وتأكيد' : 'Review & Confirm'}</h2>
                  {[
                    { label: lang === 'ar' ? 'الاسم' : 'Name', value: getValues('full_name') },
                    { label: lang === 'ar' ? 'البريد الإلكتروني' : 'Email', value: getValues('email') },
                    { label: lang === 'ar' ? 'الشركة' : 'Company', value: getValues('company_name') },
                    { label: lang === 'ar' ? 'الدولة' : 'Country', value: getValues('country') },
                    { label: lang === 'ar' ? 'المكتب' : 'Office', value: getValues('office_name') },
                    { label: lang === 'ar' ? 'المدينة' : 'City', value: getValues('office_city') },
                    { label: lang === 'ar' ? 'مناطق الخدمة' : 'Service Zones', value: selectedZones.join(', ') || '—' },
                    { label: lang === 'ar' ? 'أنواع الوقود' : 'Bunker Types', value: selectedFuels.join(', ') || '—' },
                    { label: lang === 'ar' ? 'وثيقة الهوية' : 'ID Document', value: idDocumentName || '—' },
                  ].map((row, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-border/40 text-sm">
                      <span className="text-muted-foreground">{row.label}</span>
                      <span className="text-foreground font-medium text-right max-w-48 truncate">{row.value || '—'}</span>
                    </div>
                  ))}
                  <div className="mt-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/25">
                    <p className="text-xs text-emerald-400 font-medium">{lang === 'ar' ? 'نوع الحساب: مورد' : 'Account type: Supplier'}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {lang === 'ar' ?'سيتم مراجعة طلبك من قبل فريق NautEX بعد فحص العقوبات. لن يُمنح وصول المنصة إلا بعد الموافقة.' :'Your registration will be reviewed by the NautEX team after sanctions screening. Platform access is granted only after approval.'}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-4">
              {step > 1 ? (
                <button type="button" onClick={handleBack} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
                  <ChevronLeft size={16} />{lang === 'ar' ? 'السابق' : 'Back'}
                </button>
              ) : <div />}
              {step < steps.length ? (
                <button type="button" onClick={handleNext} className="btn-primary flex items-center gap-2 px-6 py-2.5 text-sm">
                  {lang === 'ar' ? 'التالي' : 'Next'}<ChevronRight size={16} />
                </button>
              ) : (
                <button type="submit" disabled={submitting} className="btn-primary flex items-center gap-2 px-6 py-2.5 text-sm">
                  {submitting ? <><Loader2 size={16} className="animate-spin" />{lang === 'ar' ? 'جارٍ التقديم...' : 'Submitting...'}</> : <>{lang === 'ar' ? 'تقديم الطلب' : 'Submit Registration'}<CheckCircle2 size={16} /></>}
                </button>
              )}
            </div>
          </form>

          <p className="text-center text-xs text-muted-foreground mt-4">
            {lang === 'ar' ? 'لديك حساب؟' : 'Already have an account?'}{' '}
            <Link href="/login" className="text-primary hover:underline">{lang === 'ar' ? 'تسجيل الدخول' : 'Sign in'}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
